/**
 * Link-native browser client (Compose HTML).
 *
 * Human-facing view of the node's *local world*: visible posts and their threads, the inbox of recommendations routed
 * to this actor, and — first-class — the missing observations whose bodies this node cannot see. Authoring and reading
 * go through the shared [SocialClient]/[SocialBoard], the same surface the TUI and agents use, so humans and agents act
 * through identical verbs. Peers connect over WebRTC DataChannels with manual copy/paste signaling and naive
 * full-state [com.oasisfeng.invaluable.social.PeerSync]; state persists to localStorage.
 */
package com.oasisfeng.invaluable.social.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.oasisfeng.invaluable.Quantity
import com.oasisfeng.invaluable.event.DurableWrite
import com.oasisfeng.invaluable.event.RecipeWireCodec
import com.oasisfeng.invaluable.event.ReplicatedEventApplier.ApplyResult
import com.oasisfeng.invaluable.identity.Identity
import com.oasisfeng.invaluable.identity.store.BrowserStore
import com.oasisfeng.invaluable.identity.store.identityStorageKey
import com.oasisfeng.invaluable.identity.store.loadOrCreate
import com.oasisfeng.invaluable.p2p.core.LoopbackSession
import com.oasisfeng.invaluable.p2p.core.OpaqueRelaySession
import com.oasisfeng.invaluable.p2p.core.OpaqueRelaySocketAdapter
import com.oasisfeng.invaluable.p2p.core.P2pSession
import com.oasisfeng.invaluable.p2p.websocket.ReconnectPolicy
import com.oasisfeng.invaluable.p2p.webrtc.DataChannelAdapter
import com.oasisfeng.invaluable.p2p.webrtc.PeerConnectionAdapter
import com.oasisfeng.invaluable.social.ActionGrammar
import com.oasisfeng.invaluable.social.ActionOutcome
import com.oasisfeng.invaluable.social.ActorShare
import com.oasisfeng.invaluable.social.ActorTag
import com.oasisfeng.invaluable.social.AgentActionPreview
import com.oasisfeng.invaluable.social.BroadcastChannelBus
import com.oasisfeng.invaluable.social.CompositeBus
import com.oasisfeng.invaluable.social.costAwareCoalescer
import com.oasisfeng.invaluable.social.debouncingCoalescer
import com.oasisfeng.invaluable.social.InboxItem
import com.oasisfeng.invaluable.social.PeerDiscovery
import com.oasisfeng.invaluable.social.PostCard
import com.oasisfeng.invaluable.social.PostRoute
import com.oasisfeng.invaluable.social.SessionTracker
import com.oasisfeng.invaluable.social.Signaling
import com.oasisfeng.invaluable.social.SocialAction
import com.oasisfeng.invaluable.social.SocialBoard
import com.oasisfeng.invaluable.social.SocialClient
import com.oasisfeng.invaluable.social.SocialNode
import com.oasisfeng.invaluable.social.SocialRef
import com.oasisfeng.invaluable.social.SummonSession
import com.oasisfeng.invaluable.social.WebRtcPeerManager
import com.oasisfeng.invaluable.social.WebSocketBus
import com.oasisfeng.invaluable.social.formatAmount
import com.oasisfeng.invaluable.social.formatPercent
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.readOnly
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.jetbrains.compose.web.renderComposable
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

fun main() {
    val params: dynamic = js("new URLSearchParams(window.location.search)")
    val urlNs = (params.get("ns") as String?)?.takeIf { it.isNotBlank() }
    val urlName = (params.get("name") as String?)?.takeIf { it.isNotBlank() }
    // Auto-discover by default (mirrors the TUI's --no-discover); ?discover=0 opts out.
    val discover = (params.get("discover") as String?) !in listOf("0", "false", "off")
    // Localhost signaling-relay shared with Node forms (TUI/MCP). On HTTPS pages, skip it unless explicitly requested.
    val signalUrl = localSignalUrl(params.get("signal") as String?)
    val relayUrls = relayUrls(params)
    // A ?ns= deep-link binds this tab to that identity (and registers it); without it, the gate asks the user to pick.
    if (urlNs != null) { SavedIdentities.ensure(urlNs, urlName ?: urlNs); Tab.bind(urlNs) }

    val gate = AppGate(initialNs = urlNs ?: Tab.ns(), discover = discover, signalUrl = signalUrl, relayUrls = relayUrls)
    renderComposable(rootElementId = "root") { gate.Render() }
}

private const val EXPORT_FORMAT = "invaluable.social.export"
private const val IDENTITY_EXPORT_FORMAT = "invaluable.social.identity.export"
private const val DEFAULT_RELAY_URL =
    "wss://invaluable-relay-opaque.oasisfeng.workers.dev/connect?transport=opaque-v2&route=dogfooding"
private const val BOOT_DEFER_MS = 1
private const val RESTORE_YIELD_LINES = 64
private const val KNOWN_PEER_STABLE_MS = 30_000

/** Holds the node, its client/transport, and the Compose state the UI observes. */
class WebApp(
    private val ns: String,
    private val displayName: String,
    private val discover: Boolean = true,
    private val signalUrl: String? = null,
    relayUrls: List<String> = listOf(DEFAULT_RELAY_URL),
    private val onSwitchIdentity: () -> Unit = {},
    private val ensureProfileOnBoot: Boolean = true,
) {

    private val key = "invaluable.v4.$ns"
    private val sealsKey = "$key.seals"   // disclosed content-keys (device-local secrets), persisted beside the event log
    private val identity = Identity.loadOrCreate(ns)
    private val node = SocialNode(identity)
    private val scope = MainScope()
    private var bootJob: Job? = null
    private var bootStarted = false
    private val knownPeerUrls = LinkedHashSet(normalizePeerUrls(relayUrls))
    private val dialedKnownPeerUrls = HashSet<String>()
    private val webSocketPeerSessions = mutableStateListOf<KnownPeerSession>()
    // Opaque `setTimeout` handles. Deliberately `Any?`, never `dynamic`: on a dynamic receiver Kotlin/JS compiles
    // `?.let { }` into a member call `handle.let(...)` on the JS object itself, and a browser timer handle has no
    // `let`. That threw out of the socket close handler, so the reconnect below was never scheduled.
    private val knownPeerReconnectTimers = mutableMapOf<String, Any?>()
    private val knownPeerStableTimers = mutableMapOf<String, Any?>()
    private val knownPeerReconnectPolicies = mutableMapOf<String, ReconnectPolicy>()
    private var nextWebSocketSessionId = 1
    private var stopping = false

    // The summoned agent (Milestone A1) is a real, separate actor — its own identity and node — wired to the human's
    // node over an in-process LoopbackSession, so anything it authors replicates exactly as a remote peer's would.
    private val agentKey = "invaluable.v4.$ns.agent"
    private val agentSealsKey = "$agentKey.seals"
    private val agentNode = SocialNode(Identity.loadOrCreate("$ns.agent"))
    private val agentClient = SocialClient(
        node = agentNode,
        persist = { saveTo(agentKey, it) },
        render = {},
        log = { log("agent: $it") },
        persistSeals = { BrowserStore.setItem(agentSealsKey, it) },
    )
    private val summon = SummonSession(agentClient)
    private var agentLoopbackOpen = false

    var board by mutableStateOf(node.board()); private set
    private var factCount by mutableStateOf(0)
    var sessions by mutableStateOf(emptyList<String>()); private set

    /**
     * Peer links open at this moment — WebRTC DataChannels plus relay WebSocket sessions.
     *
     * The one live transport fact a node can honestly observe about itself, and the same one [ContextRenderer] gives an
     * agent. It says nothing about whether a peer has *read* anything: there are no read receipts and no presence list.
     * Kept in the header rather than behind the advanced disclosure because "did I reach the mesh at all" decides
     * whether an action you take is a commitment anyone else will ever see.
     */
    var liveLinks by mutableStateOf(0); private set
    val logs = mutableStateListOf<String>()

    var stunUrl by mutableStateOf("")
    var offerText by mutableStateOf("")
    var answerText by mutableStateOf("")
    var status by mutableStateOf(""); private set

    // ───── summon-an-agent (Milestone A1) ─────
    var summonTopic by mutableStateOf("")
    var summonPrompt by mutableStateOf(""); private set
    var summonPaste by mutableStateOf("")
    val summonRows = mutableStateListOf<PreviewRow>()
    var summonResult by mutableStateOf(""); private set

    // ───── backup: export / import this namespace's whole world (Solution 3) ─────
    // Every BrowserStore key this node owns: both identities (key material), both event logs, and both seal sidecars
    // (disclosed content-keys) — so a backup carries the ability to keep reading disclosed sealed posts (human + agent).
    private val backupKeys = listOf(identityStorageKey(ns), identityStorageKey("$ns.agent"), key, agentKey, sealsKey, agentSealsKey)
    val persistent get() = BrowserStore.persistent
    var exportText by mutableStateOf(""); private set
    var importText by mutableStateOf("")
    var identityExportText by mutableStateOf(""); private set
    var identityImportText by mutableStateOf("")
    var backupResult by mutableStateOf(""); private set

    private val client = SocialClient(
        node = node,
        persist = { saveTo(key, it) },
        render = ::refreshBoard,
        log = ::log,
        coalesce = debouncingCoalescer(),   // a late-joiner catch-up is a burst; render once it settles, not per event
        coalescePersist = costAwareCoalescer(),   // …and write on a budget, since a full re-encode makes its own silence
        persistSeals = { BrowserStore.setItem(sealsKey, it) },
    )
    private val sessionTracker = SessionTracker()
    private val peerManager = WebRtcPeerManager(
        pcAdapter = PeerConnectionAdapter.Browser,
        dcAdapter = DataChannelAdapter.Browser,
        sessionTracker = sessionTracker,
        onSessionOpened = { client.sync.onSessionOpened(it) },
        log = ::log,
        onStateChanged = { sessions = sessionTracker.formatSessions(); refreshLiveLinks() },
    )

    private fun refreshLiveLinks() {
        liveLinks = sessionTracker.openCount() + webSocketPeerSessions.count { it.session.isOpen }
    }

    // Auto-discover and connect over real WebRTC, automating the offer/answer copy/paste. Only runnable side channels
    // are enabled here; mesh signaling needs a dedicated/multiplexed sync session before it can live on this path.
    var discoveryOn by mutableStateOf(false); private set
    var discoveryStatus by mutableStateOf(emptyList<String>()); private set
    private val discoveryId = Identity.generate().peerId + ":" + (js("Math.random().toString(36).slice(2)") as String)
    private val discoveryRef = lazy {
        PeerDiscovery(
            selfId = discoveryId,
            name = displayName,
            peerManager = { peerManager },
            scope = scope,
            onChange = { refreshDiscoveryStatus() },
            log = ::log,
            bus = CompositeBus(buildList {
                add(BroadcastChannelBus())
                signalUrl?.let { add(WebSocketBus(it, log = ::log)) }
            }),
            transportCandidates = { knownPeerUrls.toList() },
            onTransportCandidates = ::addKnownPeerUrls,
        )
    }
    private val discovery: PeerDiscovery get() = discoveryRef.value

    private fun refreshDiscoveryStatus() {
        discoveryStatus = if (discoveryRef.isInitialized()) discovery.status() else emptyList()
    }

    fun boot() {
        if (bootStarted) return
        bootStarted = true
        stopping = false
        status = "Loading local world…"
        bootJob = scope.launch {
            delay(BOOT_DEFER_MS.toLong())
            runCatching { bootAsync() }.onFailure {
                if (!stopping) {
                    status = "ERR boot failed: ${it.message}"
                    log("boot failed: ${it.message}")
                }
            }
        }
    }

    fun stop() {
        stopping = true
        bootJob?.cancel()
        bootJob = null
        knownPeerReconnectTimers.values.forEach { runCatching { js("clearTimeout")(it) } }
        knownPeerReconnectTimers.clear()
        knownPeerStableTimers.values.forEach { runCatching { js("clearTimeout")(it) } }
        knownPeerStableTimers.clear()
        knownPeerReconnectPolicies.clear()
        if (discoveryRef.isInitialized()) runCatching { discovery.stop() }
        webSocketPeerSessions.toList().forEach { runCatching { it.session.close() } }
        webSocketPeerSessions.clear()
        dialedKnownPeerUrls.clear()
        refreshLiveLinks()
    }

    private suspend fun bootAsync() {
        restoreNode("your world", key, client, node)
        restoreNode("agent world", agentKey, agentClient, agentNode)
        if (stopping) return
        node.restoreSeals(BrowserStore.getItem(sealsKey)?.lineSequence().orEmpty().asIterable())
        agentNode.restoreSeals(BrowserStore.getItem(agentSealsKey)?.lineSequence().orEmpty().asIterable())
        refreshBoard()
        status = ""
        if (!BrowserStore.persistent) {
            log("WARN: storage unavailable (e.g. Safari on file://) — changes won't survive a reload. Use Backup to export your world.")
        }
        log("ready: $displayName · ns=$ns · me=${board.me.label} · agent=${summon.agentLabel}")

        // Post-restore work can scan, encode, or sync large state. Run it after the first real board has painted.
        if (ensureProfileOnBoot) {
            delay(BOOT_DEFER_MS.toLong())
            if (stopping) return
            ensureProfileAfterBoot()
        }
        delay(BOOT_DEFER_MS.toLong())
        if (stopping) return
        if (discover) {
            delay(BOOT_DEFER_MS.toLong())
            if (stopping) return
            startDiscovery()
            connectKnownPeerUrls()
        }
        log("relay peers: ${knownPeerUrls.joinToString().ifBlank { "(none)" }}")
    }

    /**
     * Restore one node's persisted world through the shared, batch-validated intake.
     *
     * [SocialClient.restoreInSlices] feeds the stored lines to the same call-spanning batch stager every peer's intake
     * uses — so a reload validates the batch-v1 envelope exactly as a peer would — and yields a macrotask between
     * slices, keeping the tab answerable while a long log replays. Blobs written before batch-v1 became mandatory are
     * bare recipe lines, which that intake ignores by design; a store that lands nothing is therefore decoded once
     * through the line codec and immediately re-saved in the current wire form, rather than silently opening the tab
     * on an empty world.
     */
    private suspend fun restoreNode(label: String, storageKey: String, target: SocialClient, node: SocialNode) {
        val serialized = BrowserStore.getItem(storageKey)?.takeIf { it.isNotBlank() } ?: return
        val before = countFacts(node)
        var records = 0
        target.restoreInSlices(serialized) { restored, total ->
            records = total
            status = "Loading $label: $restored/$total records…"
        }
        if (stopping) return
        val applied = countFacts(node) - before
        if (applied > 0) { log("restored $label: $records records, $applied new facts"); return }
        val legacy = restoreLegacyLines(serialized, node)
        if (stopping || legacy == 0) return
        // What is stored is the pre-batch blob itself, so this has to replace it rather than extend it.
        target.persistWholeState()
        log("restored $label from a pre-batch save: $legacy new facts (re-saved in the current wire form)")
    }

    /**
     * Put one write into `localStorage`, which has no append of its own — so an extension is read, concatenate, write.
     *
     * Still worth doing: what a full re-encode costs is deriving the text (rebuilding every record's Link, re-verifying
     * every regenerable closure against its head), not moving it. Re-reading a couple of megabytes of string to put
     * something on the end of it is nothing beside re-deriving them.
     */
    private fun saveTo(storageKey: String, write: DurableWrite): Boolean {
        BrowserStore.setItem(storageKey, when (write) {
            is DurableWrite.Whole -> write.text
            is DurableWrite.Appended -> (BrowserStore.getItem(storageKey) ?: "") + write.text
        })
        return true
    }

    /** Decode a pre-batch blob (bare recipe lines) event by event; returns how many facts it landed. */
    private suspend fun restoreLegacyLines(serialized: String, target: SocialNode): Int {
        val codec = RecipeWireCodec(target.projector)
        var lines = 0
        var applied = 0
        for (line in serialized.lineSequence()) {
            if (stopping) return applied
            for (event in codec.decodeLine(line)) if (target.apply(event) is ApplyResult.Applied) applied++
            if (++lines % RESTORE_YIELD_LINES == 0) delay(BOOT_DEFER_MS.toLong())
        }
        return applied
    }

    private fun ensureProfileAfterBoot() {
        // Identity-only imports may need relay catch-up before local authoring; callers can opt out.
        // Through the client, so this write extends the saved copy rather than replacing it — and repaints on its way.
        client.ensureLocalProfile(displayName, Quantity.ONE)
    }

    private fun refreshBoard() {
        board = node.board()
        factCount = countFacts()
    }

    private fun countFacts(target: SocialNode = node): Int =
        target.store.preimages().count() + target.store.attestations().count() + target.store.sealedContents().count()

    /**
     * Wire the in-browser agent node to the human node so their commitments replicate both ways (frontier catch-up).
     *
     * Opened on first use of the Summon card, not at boot. The loopback is a real delta-chain session, so opening it
     * replays this node's entire world into the agent's separate store — on a dog-fooding world that is the single
     * heaviest job on the tab's one thread, and every boot paid it for an agent most sessions never summon. Deferring
     * costs the agent nothing: [LoopbackSession] delivers synchronously, so the first prompt still reads a fully
     * caught-up agent world.
     */
    private fun ensureAgentLoopback() {
        if (agentLoopbackOpen) return
        agentLoopbackOpen = true
        val (toAgent, toHuman) = LoopbackSession.pair()
        client.sync.onSessionOpened(toAgent)
        agentClient.sync.onSessionOpened(toHuman)
    }

    fun startDiscovery() { discovery.start(); discoveryOn = discovery.isRunning; refreshDiscoveryStatus() }
    fun stopDiscovery() { discovery.stop(); discoveryOn = false; refreshDiscoveryStatus() }
    fun toggleDiscovery() {
        if (discoveryOn) stopDiscovery() else {
            startDiscovery()
            connectKnownPeerUrls()
        }
    }

    fun perform(action: SocialAction) {
        status = when (val outcome = client.perform(action)) {
            // Name the object the action produced — the post just authored, or the route a recommend opened, which is
            // your own asset from that moment on. The same receipt an agent gets back from its tool call.
            is ActionOutcome.Done -> "OK ${outcome.message}" + (outcome.ref?.let { " → [$it]" } ?: "")
            is ActionOutcome.Failed -> "ERR ${outcome.message}"
        }
    }

    fun generatePrompt() {
        ensureAgentLoopback()   // the agent reads its own world, so catch it up before rendering the prompt
        summonPrompt = summon.prompt(summonTopic.takeIf { it.isNotBlank() }?.let(::SocialRef))
        summonResult = ""
    }

    fun previewPaste() {
        ensureAgentLoopback()
        summonRows.clear()
        summon.preview(summonPaste).forEach { summonRows.add(PreviewRow(it)) }
        summonResult = if (summonRows.isEmpty()) "Nothing to preview — paste the LLM's action lines first." else ""
    }

    fun executeConfirmed() {
        ensureAgentLoopback()   // whatever the agent authors must replicate out through a live session
        val actions = summonRows.filter { it.confirmed }.mapNotNull { it.preview.action }
        if (actions.isEmpty()) { summonResult = "No actions confirmed."; return }
        val done = summon.execute(actions).count { it is ActionOutcome.Done }
        summonResult = "Executed $done/${actions.size} as ${summon.agentLabel}."
        summonPaste = ""; summonPrompt = ""; summonRows.clear()
        refreshBoard()   // the agent's commitments have already arrived over the loopback
    }

    private fun copyPrompt() { runCatching { window.navigator.asDynamic().clipboard.writeText(summonPrompt) } }

    private fun addKnownPeerUrls(urls: List<String>) {
        var changed = false
        for (url in normalizePeerUrls(urls)) changed = knownPeerUrls.add(url) || changed
        if (changed && discoveryOn) connectKnownPeerUrls()
    }

    private fun connectKnownPeerUrls() {
        if (stopping) return
        for (url in knownPeerUrls) {
            if (!dialedKnownPeerUrls.add(url)) continue
            runCatching {
                openKnownPeerSession(url, "social") { client.sync.onSessionOpened(it) }
                log("relay: dialed $url")
            }.onFailure {
                dialedKnownPeerUrls.remove(url)
                log("relay: failed to dial $url: ${it.message}")
                scheduleKnownPeerReconnect(url, "social")
            }
        }
    }

    private fun openKnownPeerSession(url: String, role: String, onOpen: (P2pSession) -> Unit) {
        require(OpaqueRelaySession.isUrl(url)) { "Relay URLs must use transport=opaque-v2 with a valid route" }
        var session: P2pSession? = null
        val cursorKey = opaqueRelayCursorKey(url)
        val socket = js("Reflect").construct(
            js("WebSocket"), arrayOf<Any?>(url, OpaqueRelaySession.PROTOCOL),
        )
        val created: P2pSession = OpaqueRelaySession(
            id = nextWebSocketSessionId++,
            route = checkNotNull(OpaqueRelaySession.routeFromUrl(url)),
            projector = node.projector,
            initialCursor = BrowserStore.getItem(cursorKey)?.toLongOrNull() ?: 0,
            confirmSemanticDurability = client::confirmRemoteDurability,
            onCursorChanged = { BrowserStore.setItem(cursorKey, it.toString()) },
            socket = socket,
            adapter = OpaqueRelaySocketAdapter.Dom,
            log = { log("relay $role: $it") },
            onStateChanged = { session?.let { onKnownPeerStateChanged(url, role, it) } },
        )
        session = created
        webSocketPeerSessions += KnownPeerSession(url, role, created)
        onOpen(created)
    }

    private fun onKnownPeerStateChanged(url: String, role: String, session: P2pSession) {
        sessions = sessionTracker.formatSessions()
        refreshLiveLinks()
        refreshDiscoveryStatus()
        if (session.isOpen) {
            scheduleKnownPeerStableReset(url, role, session)
            return
        }
        if (! isKnownPeerClosed(session)) return
        knownPeerStableTimers.remove(url)?.let { timer -> runCatching { js("clearTimeout")(timer) } }
        webSocketPeerSessions.removeAll { it.session === session }
        dialedKnownPeerUrls.remove(url)
        scheduleKnownPeerReconnect(url, role)
    }

    private fun scheduleKnownPeerStableReset(url: String, role: String, session: P2pSession) {
        knownPeerStableTimers.remove(url)?.let { timer -> runCatching { js("clearTimeout")(timer) } }
        knownPeerStableTimers[url] = js("setTimeout")(fun() {
            knownPeerStableTimers.remove(url)
            if (session.isOpen) {
                knownPeerReconnectPolicies.getOrPut(url) { ReconnectPolicy() }.onConnected()
                log("relay $role: stable; reset reconnect policy for $url")
            }
        }, KNOWN_PEER_STABLE_MS)
    }

    private fun scheduleKnownPeerReconnect(url: String, role: String) {
        if (stopping || !discoveryOn || url !in knownPeerUrls) return
        if (knownPeerReconnectTimers.containsKey(url)) return
        val policy = knownPeerReconnectPolicies.getOrPut(url) { ReconnectPolicy() }
        val delayMs = policy.onFailure()
        val state = if (policy.isCircuitOpen) "circuit open" else "retry #${policy.consecutiveFailures}"
        log("relay $role: $state; retrying $url in ${delayMs}ms")
        knownPeerReconnectTimers[url] = js("setTimeout")(fun() {
            knownPeerReconnectTimers.remove(url)
            log("relay $role: reconnecting $url")
            connectKnownPeerUrls()
        }, delayMs)
    }

    private fun relaySessionLines(): List<String> {
        val count = webSocketPeerSessions.count { it.session.isOpen }
        return listOf("relay sessions: $count/${webSocketPeerSessions.size}") + webSocketPeerSessions.map {
            "  ${it.role} ${it.url} ${if (it.session.isOpen) "open" else "closed"}"
        }
    }

    // ───── backup: export / import (Solution 3) ─────

    /** Serialize this namespace's identities + event logs into one JSON blob, shown for copy and offered as download. */
    fun exportWorld() {
        val keys: dynamic = js("({})")
        backupKeys.forEach { k -> BrowserStore.getItem(k)?.let { keys[k] = it } }
        val obj: dynamic = js("({})")
        obj.format = EXPORT_FORMAT
        obj.version = 1
        obj.ns = ns
        obj.keys = keys
        exportText = js("JSON").stringify(obj, null, 2) as String
        backupResult = "Exported — copy the text below, or download the file."
    }

    fun downloadExport() {
        if (exportText.isBlank()) exportWorld()
        val parts: dynamic = js("[]")
        parts.push(exportText)
        val url = URL.createObjectURL(Blob(parts, BlobPropertyBag(type = "application/json")))
        val a = document.createElement("a") as HTMLAnchorElement
        a.href = url
        a.setAttribute("download", "invaluable-$ns.json")
        a.click()
        URL.revokeObjectURL(url)
    }

    private fun copyExport() { runCatching { window.navigator.asDynamic().clipboard.writeText(exportText) } }

    /** Export only signing identities, so another origin can adopt the same actor without importing this world log. */
    fun exportIdentityCredentials() {
        val keys: dynamic = js("({})")
        BrowserStore.getItem(identityStorageKey(ns))?.let { keys.human = it }
        BrowserStore.getItem(identityStorageKey("$ns.agent"))?.let { keys.agent = it }
        val obj: dynamic = js("({})")
        obj.format = IDENTITY_EXPORT_FORMAT
        obj.version = 1
        obj.sourceNs = ns
        obj.displayName = displayName
        obj.keys = keys
        identityExportText = js("JSON").stringify(obj, null, 2) as String
        backupResult = "Identity credentials exported — copy the text and import it on the other origin."
    }

    private fun copyIdentityExport() { runCatching { window.navigator.asDynamic().clipboard.writeText(identityExportText) } }

    /** Import signing identities into this namespace; reload is required because the live node already holds its key. */
    fun importIdentityCredentials() {
        val text = identityImportText.trim()
        if (text.isBlank()) { backupResult = "Paste exported identity credentials first."; return }
        val parsed = runCatching { js("JSON").parse(text) }
            .getOrElse { backupResult = "Identity import failed: not valid JSON."; return }
        if (parsed.format != IDENTITY_EXPORT_FORMAT) {
            backupResult = "Identity import failed: unrecognized credentials format."
            return
        }
        val keys = parsed.keys ?: run { backupResult = "Identity import failed: no credentials in blob."; return }
        val human = keys.human as? String ?: run { backupResult = "Identity import failed: missing human identity."; return }
        BrowserStore.setItem(identityStorageKey(ns), human)
        (keys.agent as? String)?.let { BrowserStore.setItem(identityStorageKey("$ns.agent"), it) }
        identityImportText = ""
        backupResult = if (persistent) "Identity credentials imported into '$ns'. Reload to adopt them."
        else "Identity credentials imported into this session, but storage is not persistent."
    }

    /** Restore a previously exported blob: write its keys back to storage, then merge the event logs into the live world. */
    fun importWorld() {
        val text = importText.trim()
        if (text.isBlank()) { backupResult = "Paste an exported blob first."; return }
        val parsed = runCatching { js("JSON").parse(text) }
            .getOrElse { backupResult = "Import failed: not valid JSON."; return }
        if (parsed.format != EXPORT_FORMAT) { backupResult = "Import failed: unrecognized format."; return }
        val keys = parsed.keys ?: run { backupResult = "Import failed: no keys in blob."; return }
        val names = js("Object").keys(keys) as Array<String>
        names.forEach { k -> (keys[k] as? String)?.let { BrowserStore.setItem(k, it) } }
        // Grow-only union: merging the saved logs surfaces the world immediately, no reload needed for content.
        client.restore(BrowserStore.getItem(key))
        agentClient.restore(BrowserStore.getItem(agentKey))
        client.restoreSeals(BrowserStore.getItem(sealsKey))
        agentClient.restoreSeals(BrowserStore.getItem(agentSealsKey))
        // The blob replaced what was stored, but each live node holds the union of that and what it already had. Only a
        // whole write says so; an append would leave everything from before the import on no disk at all.
        client.persistWholeState()
        agentClient.persistWholeState()
        refreshBoard()
        importText = ""
        backupResult = if (persistent) "Imported ${names.size} key(s). Reload to also adopt the imported identity."
        else "Imported ${names.size} key(s) into this session (storage is not persistent)."
    }

    /** Every visible post (feed + threaded replies), flattened for the topic selector. */
    private fun visiblePosts(): List<PostCard> {
        val out = mutableListOf<PostCard>()
        fun walk(cards: List<PostCard>) { cards.forEach { out += it; walk(it.replies) } }
        walk(board.feed)
        return out
    }

    private fun log(message: String) {
        logs.add(message)
        if (logs.size > 40) logs.removeAt(0)
    }

    private fun signaling(label: String, action: suspend () -> Unit) = scope.launch {
        runCatching { action() }.onFailure { status = "$label failed: ${it.message}" }
    }

    fun createOffer() = signaling("Create offer") {
        peerManager.stunUrl = stunUrl
        offerText = peerManager.createOffer { offerText = it }
        status = "Offer ready — copy it to a peer."
    }

    fun createAnswer() = signaling("Create answer") {
        peerManager.stunUrl = stunUrl
        answerText = peerManager.acceptOffer(offerText.trim()) { answerText = it }
        status = "Answer ready — copy it back to the offerer."
    }

    fun applyAnswer() = signaling("Apply answer") {
        peerManager.stunUrl = stunUrl
        peerManager.applyAnswer(answerText.trim())
        status = "Answer applied — waiting for the channel to open."
    }

    @Composable
    fun Render() {
        Div(attrs = { classes("app") }) {
            Div(attrs = { classes("header") }) {
                Div(attrs = { classes("post-head") }) {
                    Div(attrs = { classes("brand") }) {
                        H1 { Text("Invaluable") }
                        Span(attrs = { classes("subtitle") }) { Text("your local world") }
                    }
                    Button(attrs = { classes("ghost"); onClick { onSwitchIdentity() } }) { Text("Switch identity") }
                }
                Div(attrs = { classes("chips") }) {
                    Chip("you", board.me.displayName ?: displayName)
                    Chip("actor", board.me.ref.value)
                    Chip("tab", "@$ns")
                    Chip("facts", factCount.toString())
                    Chip(
                        "live links", liveLinks.toString(),
                        hint = "Peer links open right now (WebRTC + relay). What you author propagates over these. " +
                            "There are no read receipts: an open link never means anyone has read you.",
                    )
                }
                if (status.isNotBlank()) {
                    val kind = when {
                        status.startsWith("OK") -> "ok"
                        status.startsWith("ERR") -> "err"
                        else -> "warn"
                    }
                    Div(attrs = { classes("status", kind) }) { Text(status) }
                }
                if (!persistent) Div(attrs = { classes("status", "warn") }) {
                    Text("⚠ storage unavailable (e.g. Safari on file://) — changes won't survive a reload. Use Backup to export.")
                }
            }
            Div(attrs = { classes("columns") }) {
                Div(attrs = { classes("col") }) {
                    Compose()
                    Feed()
                }
                Div(attrs = { classes("col", "side") }) {
                    Inbox()
                    Summon()
                    Economy()
                    Missing()
                    Collapsible("Advanced — connect a peer & backup") {
                        Connect()
                        Backup()
                    }
                    NetLog()
                }
            }
        }
    }

    /** A small labelled key/value pill used in the header — turns the cryptic meta line into scannable facts. */
    @Composable
    private fun Chip(key: String, value: String, hint: String? = null) {
        Span(attrs = { classes("chip"); hint?.let { title(it) } }) {
            Span(attrs = { classes("chip-k") }) { Text(key) }
            Span(attrs = { classes("chip-v") }) { Text(value) }
        }
    }

    /** A click-to-expand section header; its body (power-user cards) stays hidden until asked for. */
    @Composable
    private fun Collapsible(title: String, body: @Composable () -> Unit) {
        var open by remember { mutableStateOf(false) }
        Div(attrs = { classes("disclosure"); onClick { open = !open } }) {
            Span(attrs = { classes("disclosure-caret") }) { Text(if (open) "▾" else "▸") }
            Span { Text(title) }
        }
        if (open) body()
    }

    @Composable
    private fun Compose() {
        var text by remember { mutableStateOf("") }
        var cost by remember { mutableStateOf("1") }
        Card("Share something") {
            TextArea(value = text, attrs = { classes("compose"); placeholder("What's on your mind?"); onInput { text = it.value } })
            Div(attrs = { classes("row", "compose-row") }) {
                CostField(cost) { cost = it }
                Span(attrs = { classes("hint") }) { Text("stake you put behind it") }
                Button(attrs = {
                    classes("primary")
                    title("Publish this post, staking the cost as your backing")
                    onClick {
                        val q = quantity(cost) ?: return@onClick
                        if (text.isNotBlank()) { perform(SocialAction.PostText(text.trim(), q)); text = "" }
                    }
                }) { Text("Post") }
            }
        }
    }

    @Composable
    private fun Feed() {
        Card("Feed (${board.feed.size})") {
            if (board.feed.isEmpty()) Empty("Nothing visible yet. Post, or connect to a peer.")
            else board.feed.forEach { PostView(it) }
        }
    }

    @Composable
    private fun PostView(card: PostCard) {
        Div(attrs = { classes("post") }) {
            Div(attrs = { classes("post-head") }) {
                Author(card.author)
                Span(attrs = { classes("ref") }) { Text(card.ref.value) }
            }
            P { Text(card.text ?: "(non-text content)") }
            Div(attrs = { classes("stats") }) {
                Stat("cost", card.cost.toString())
                Stat("backing", card.backingTotal.toString())
                card.value?.let { Stat("value", formatAmount(it)) }
                if (card.yourShare > 0.0) ShareStat(card)
            }
            PostRoutes(card)
            PostActions(card)
            PushSuggestions(card)
            if (card.replies.isNotEmpty()) Div(attrs = { classes("replies") }) { card.replies.forEach { PostView(it) } }
        }
    }

    /** Lead with the human-chosen display name; the content-addressed handle is shown separately, de-emphasized. */
    @Composable
    private fun Author(tag: ActorTag) {
        Span(attrs = { classes("author-line") }) {
            Span(attrs = { classes("author") }) { Text(tag.displayName ?: tag.ref.value) }
            if (tag.displayName != null) Span(attrs = { classes("author-ref") }) { Text(tag.ref.value) }
            if (tag.isMe) Span(attrs = { classes("badge") }) { Text("you") }
        }
    }

    /** A labelled metric pill (cost / backing / value / share) — replaces the cryptic single-line meta string. */
    @Composable
    private fun Stat(key: String, value: String) {
        Span(attrs = { classes("stat") }) {
            Span(attrs = { classes("stat-k") }) { Text(key) }
            Span(attrs = { classes("stat-v") }) { Text(value) }
        }
    }

    @Composable
    private fun ShareStat(card: PostCard) {
        var open by remember(card.ref.value) { mutableStateOf(false) }
        Span(attrs = { classes("stat", "stat-button"); title("Show visible ownership shares"); onClick { open = !open } }) {
            Span(attrs = { classes("stat-k") }) { Text("your share") }
            Span(attrs = { classes("stat-v") }) { Text(formatPercent(card.yourShare)) }
        }
        if (open) {
            val others = card.actorShares.filterNot { it.actor.isMe }
            Div(attrs = { classes("share-breakdown") }) {
                if (others.isEmpty()) Span(attrs = { classes("muted") }) { Text("No other visible shares.") }
                else others.forEach { ShareLine(it) }
            }
        }
    }

    @Composable
    private fun ShareLine(share: ActorShare) {
        Span(attrs = { classes("share-line") }) {
            ActorName(share.actor)
            Span(attrs = { classes("ref") }) { Text(formatPercent(share.share)) }
        }
    }

    /**
     * The visible routes carrying this post's attention — each one the sender's own asset, not a decoration on the post.
     *
     * `recommend` mints an ownable, appreciating claim, so a chip reads what the board already values for it: the
     * route's net worth, the share of it you hold, and whether the recipient engaged — a route appreciates on their
     * endorse or reply, never on being sent.
     */
    @Composable
    private fun PostRoutes(card: PostCard) {
        if (card.routes.isEmpty()) return
        var open by remember(card.ref.value) { mutableStateOf(false) }
        val shown = if (open) card.routes else card.routes.takeLast(3)
        Div(attrs = { classes("route-strip") }) {
            Button(attrs = {
                classes("ghost", "route-toggle")
                title(if (open) "Collapse visible ActorRoutes" else "Show all visible ActorRoutes for this post")
                onClick { open = !open }
            }) { Text(if (open) "routes ${card.routes.size} ↑" else "routes ${card.routes.size} ↓") }
            shown.forEach { route ->
                Span(attrs = { classes("route-chip"); title(routeHint(route)) }) {
                    ActorName(route.from)
                    Span(attrs = { classes("route-arrow") }) { Text("→") }
                    ActorName(route.to)
                    route.value?.let { Span(attrs = { classes("route-econ") }) { Text("· ${formatAmount(it)}") } }
                    if (route.yourShare > 0.0) Span(attrs = { classes("route-econ") }) { Text("· you ${formatPercent(route.yourShare)}") }
                    Span(attrs = { classes("route-state", if (route.answered) "engaged" else "pending") }) {
                        Text(if (route.answered) "engaged" else "no reply yet")
                    }
                }
            }
        }
    }

    /** Everything a route says about itself, for the chip's tooltip — who paid, how much, what it is worth, whether it landed. */
    private fun routeHint(route: PostRoute): String = buildString {
        append(if (route.from.isMe) "You routed this post to ${route.to.label}" else "${route.from.label} routed this post to ${route.to.label}")
        append("; staked ${route.cost}")
        route.value?.let { append("; route value ${formatAmount(it)} in your currency") }
        if (route.yourShare > 0.0) append("; your share of it ${formatPercent(route.yourShare)}")
        append(
            if (route.answered) "; the recipient engaged — endorsed it or replied through it"
            else "; no response yet — a route appreciates on the recipient's engagement, not on being sent",
        )
        append("; route [${route.ref}]")
    }

    /** A display name is an unauthenticated label, so the content-addressed handle stays one hover away, never lost. */
    @Composable
    private fun ActorName(tag: ActorTag) {
        Span(attrs = { classes("actor-name"); title(tag.label) }) { Text(tag.displayName ?: tag.ref.value) }
    }

    /**
     * Milestone 8: likely backers to recommend this post to, one click each (the route is your own asset). Offered on
     * any visible post, held or not — paying for the route buys a share of the post it routes.
     */
    @Composable
    private fun PushSuggestions(card: PostCard) {
        val suggestions = board.suggestedRoutes.filter { it.subjectRef == card.ref }
        if (suggestions.isEmpty()) return
        Div(attrs = { classes("actions") }) {
            Span(attrs = { classes("muted") }) { Text("suggest push:") }
            suggestions.forEach { s ->
                Button(attrs = {
                    title("${s.reason}; stakes ${s.suggestedCost}")
                    onClick { perform(SocialAction.Recommend(card.ref, s.to.ref, s.suggestedCost)) }
                }) { Text("→ ${s.to.label}") }
            }
        }
    }

    @Composable
    private fun PostActions(card: PostCard) {
        var reply by remember(card.ref.value) { mutableStateOf("") }
        var cost by remember(card.ref.value) { mutableStateOf("1") }
        var recommendTo by remember(card.ref.value) { mutableStateOf("") }
        Div(attrs = { classes("actions") }) {
            Input(type = InputType.Text, attrs = { placeholder("reply…"); value(reply); onInput { reply = it.value } })
            CostField(cost) { cost = it }
            Button(attrs = {
                title("Reply publicly under this post, staking the cost")
                onClick {
                    val q = quantity(cost) ?: return@onClick
                    if (reply.isNotBlank()) { perform(SocialAction.Comment(card.ref, reply.trim(), q)); reply = "" }
                }
            }) { Text("Comment") }
            Button(attrs = {
                title("Add backing (stake) to this post — raises its standing and your share of its value")
                onClick { quantity(cost)?.let { perform(SocialAction.Back(card.ref, it)) } }
            }) { Text("Back") }
            val others = board.actors.filter { !it.isMe }
            if (others.isNotEmpty()) {
                Select(attrs = {
                    classes("recommend-select")
                    title("Route this post into someone's inbox, staking the current cost")
                    onChange {
                        recommendTo = it.value.orEmpty()
                        val q = quantity(cost) ?: return@onChange
                        if (recommendTo.isNotBlank()) {
                            perform(SocialAction.Recommend(card.ref, SocialRef(recommendTo), q))
                            recommendTo = ""
                        }
                    }
                }) {
                    Option("") { Text("Recommend to…") }
                    // Label, not display name: a display name is unauthenticated and may legitimately repeat, so the
                    // only safe target for a staked route is the exact [ref] (ContextRenderer warns agents of the same).
                    others.forEach { Option(it.ref.value) { Text(it.label) } }
                }
            }
        }
    }

    /**
     * The routes you opened — what happened to the attention you paid for.
     *
     * An agent's `get_context` prints only the routes addressed *to* it, so it can appraise a route it owns but cannot
     * enumerate one (docs/dog-fooding.md, E1's hardest failure mode). A front-end holds the whole board and has no such
     * hole: `SocialBoard.routes` carries every visible route, so listing your own here projects a reading the board has
     * already solved rather than adding one.
     */
    @Composable
    private fun YourRoutes() {
        val mine = board.routes.filter { it.from.isMe }
        if (mine.isEmpty()) return
        Div(attrs = { classes("muted") }) {
            Text("Attention you staked. Each recommend opens a route you own; it appreciates when the recipient engages, not when it is sent.")
        }
        mine.forEach { route ->
            Div(attrs = { classes("econ-row") }) {
                Div(attrs = { classes("post-head") }) {
                    Span(attrs = { classes("author-line") }) {
                        Span(attrs = { classes("route-arrow") }) { Text("→") }
                        ActorName(route.to)
                    }
                    Span(attrs = { classes("ref"); title(routeHint(route)) }) { Text(route.ref.value) }
                }
                Div(attrs = { classes("muted") }) {
                    Text(board.card(route.subjectRef)?.text?.let { if (it.length > 72) it.take(69) + "…" else it }
                        ?: "post [${route.subjectRef}] (not visible from here)")
                }
                Div(attrs = { classes("stats") }) {
                    Stat("cost", route.cost.toString())
                    route.value?.let { Stat("value", formatAmount(it)) }
                    if (route.yourShare > 0.0) Stat("your share", formatPercent(route.yourShare))
                    Stat("recipient", if (route.answered) "engaged" else "no reply yet")
                }
            }
        }
        Div(attrs = { classes("section-rule") }) {}
    }

    @Composable
    private fun Economy() {
        Card("Economy") {
            YourRoutes()
            if (!board.valuation.priced) {
                Empty("No priced commitments yet — currency prices appear once you've staked something.")
            } else board.actors.forEach { tag ->
                val price = board.valuation.priceOf(tag.ref)?.let(::formatAmount) ?: "—"
                val assets = board.valuation.assetsOf(tag.ref)?.let(::formatAmount) ?: "—"
                val issuance = board.valuation.issuanceOf(tag.ref)?.let(::formatAmount) ?: "0"
                Div(attrs = { classes("econ-row") }) {
                    Author(tag)
                    Div(attrs = { classes("stats") }) {
                        Stat("price", price)
                        Stat("assets", assets)
                        Stat("issuance", issuance)
                    }
                }
            }
        }
    }

    /**
     * Routes addressed to you, split the way [com.oasisfeng.invaluable.social.ContextRenderer] splits them for an agent.
     *
     * A route is a commitment: replying to it or endorsing it adds a new commitment and never removes the route, so
     * without the board's derived `acted` the Inbox would keep re-presenting everything you already handled as if it
     * still wanted an answer.
     */
    @Composable
    private fun Inbox() {
        val pending = board.inbox.filterNot { it.acted }
        val handled = board.inbox.filter { it.acted }
        Card("Inbox (${pending.size})") {
            if (pending.isEmpty()) Empty("No recommendations awaiting your response.")
            else pending.forEach { InboxView(it) }
            if (handled.isNotEmpty()) Collapsible("Recently acted (${handled.size})") {
                Div(attrs = { classes("muted") }) {
                    Text("You already replied to or endorsed these — kept so you don't lose track of what you handled.")
                }
                handled.forEach { InboxView(it) }
            }
        }
    }

    @Composable
    private fun InboxView(item: InboxItem) {
        var reply by remember(item.ref.value) { mutableStateOf("") }
        var cost by remember(item.ref.value) { mutableStateOf("1") }
        Div(attrs = { classes("post") }) {
            Div(attrs = { classes("muted") }) {
                Text("[${item.ref}] from ${item.from.label} → ${item.subject?.text ?: "post [${item.subjectRef}] (not visible)"} · cost ${item.cost}")
            }
            Div(attrs = { classes("actions") }) {
                Input(type = InputType.Text, attrs = { placeholder("reply…"); value(reply); onInput { reply = it.value } })
                CostField(cost) { cost = it }
                Button(attrs = {
                    title("Reply to this recommendation, staking the cost")
                    onClick {
                        val q = quantity(cost) ?: return@onClick
                        if (reply.isNotBlank()) { perform(SocialAction.Reply(item.ref, reply.trim(), q)); reply = "" }
                    }
                }) { Text("Reply") }
                Button(attrs = {
                    title("Vouch for this recommendation by staking — you back the route and what it points at")
                    onClick { quantity(cost)?.let { perform(SocialAction.Endorse(item.ref, it)) } }
                }) { Text("Endorse") }
            }
        }
    }

    @Composable
    private fun Missing() {
        Card("Missing (${board.missing.size})") {
            Div(attrs = { classes("muted") }) { Text("Not lost — these are bodies your node cannot currently see.") }
            if (board.missing.isEmpty()) Empty("None.")
            else board.missing.forEach { notice ->
                val who = notice.issuer?.label ?: "an unknown actor"
                // Two different dark forms: a body never sent here, versus a ciphertext this node custodies but has no
                // key for. Only the second one can be opened by a disclosure, so they must not read the same.
                Div(attrs = { classes("post") }) {
                    Text(
                        if (notice.sealed) "$who committed something your node holds sealed — the ciphertext is here, the key is not; a disclosure would open it."
                        else "$who committed something whose body is withheld here.",
                    )
                }
            }
        }
    }

    @Composable
    private fun Summon() {
        Card("Summon an agent") {
            Div(attrs = { classes("muted") }) {
                Text("Acts as ${summon.agentLabel}, a separate actor. Copy the prompt into any LLM, paste its reply back, confirm, execute — no API key, no server.")
            }
            Div(attrs = { classes("row") }) {
                Select(attrs = { onChange { summonTopic = it.value.orEmpty() } }) {
                    Option("") { Text("(whole world)") }
                    visiblePosts().forEach { card ->
                        val text = (card.text ?: "(non-text)").let { if (it.length > 36) it.take(33) + "…" else it }
                        Option(card.ref.value) { Text("[${card.ref}] $text") }
                    }
                }
                Button(attrs = { classes("primary"); onClick { generatePrompt() } }) { Text("Generate prompt") }
            }
            if (summonPrompt.isNotBlank()) {
                TextArea(value = summonPrompt, attrs = { classes("mono"); readOnly() })
                Div(attrs = { classes("row") }) {
                    Button(attrs = { onClick { copyPrompt() } }) { Text("Copy prompt") }
                    Span(attrs = { classes("muted") }) { Text(if (summon.turns > 0) "continuation (turn ${summon.turns + 1})" else "first turn") }
                }
            }
            TextArea(value = summonPaste, attrs = { classes("mono"); placeholder("paste the LLM's action lines here"); onInput { summonPaste = it.value } })
            Div(attrs = { classes("row") }) {
                Button(attrs = { classes("primary"); onClick { previewPaste() } }) { Text("Preview actions") }
                if (summonRows.any { it.confirmed && it.preview.executable }) {
                    Button(attrs = { onClick { executeConfirmed() } }) { Text("Execute confirmed") }
                }
            }
            summonRows.forEach { PreviewRowView(it) }
            if (summonResult.isNotBlank()) Div(attrs = { classes("status") }) { Text(summonResult) }
        }
    }

    @Composable
    private fun PreviewRowView(row: PreviewRow) {
        Div(attrs = { classes("post") }) {
            P { Text(row.preview.line) }
            Div(attrs = { classes("actions") }) {
                if (row.preview.executable) {
                    Button(attrs = { onClick { row.confirmed = !row.confirmed } }) {
                        Text(if (row.confirmed) "✓ included" else "excluded")
                    }
                } else {
                    Span(attrs = { classes("muted") }) { Text("✗ ${row.preview.note}") }
                }
            }
        }
    }

    @Composable
    private fun Connect() {
        Card("Connect a peer (WebRTC & relay)") {
            Div(attrs = { classes("row") }) {
                Button(attrs = {
                    title("Governs both automatic paths: same-origin tab discovery and the relay dial/reconnect")
                    onClick { toggleDiscovery() }
                }) {
                    Text(if (discoveryOn) "Auto-connect: on" else "Auto-connect: off")
                }
                Span(attrs = { classes("muted") }) {
                    Text(
                        "Same-origin tabs find each other over WebRTC, and the relay peer stays dialed and reconnected. " +
                            "Turning it off stops both — sessions already open stay open, but nothing new is dialed.",
                    )
                }
            }
            discoveryStatus.forEach { Div(attrs = { classes("muted") }) { Text(it) } }
            relaySessionLines().forEach { Div(attrs = { classes("muted") }) { Text(it) } }
            Div(attrs = { classes("muted") }) {
                Text(
                    "Manual copy/paste signaling (below) is for cross-machine peers — no signaling server needed. " +
                        "Same machine: leave STUN blank. Across machines/NATs: add a STUN url (and a TURN server " +
                        "when no direct path can be found).",
                )
            }
            Input(type = InputType.Text, attrs = { classes("mono"); placeholder("optional STUN url"); value(stunUrl); onInput { stunUrl = it.value } })
            TextArea(value = offerText, attrs = { classes("mono"); placeholder("offer blob"); onInput { offerText = it.value } })
            TextArea(value = answerText, attrs = { classes("mono"); placeholder("answer blob"); onInput { answerText = it.value } })
            Div(attrs = { classes("row") }) {
                Button(attrs = { onClick { createOffer() } }) { Text("Create offer") }
                Button(attrs = { onClick { createAnswer() } }) { Text("Create answer") }
                Button(attrs = { onClick { applyAnswer() } }) { Text("Apply answer") }
            }
            Div(attrs = { classes("muted") }) {
                if (sessions.isEmpty()) Text("No sessions.") else sessions.forEach { Div { Text(it) } }
            }
        }
    }

    @Composable
    private fun Backup() {
        Card("Backup (export / import)") {
            Div(attrs = { classes("muted") }) {
                Text(
                    "Your world lives in this browser, keyed by file path — easy to lose. Export bundles both identities " +
                        "(private keys) and all posts into one JSON; keep it safe. Import merges a saved world back in.",
                )
            }
            Div(attrs = { classes("row") }) {
                Button(attrs = { onClick { exportWorld() } }) { Text("Export") }
                if (exportText.isNotBlank()) {
                    Button(attrs = { onClick { downloadExport() } }) { Text("Download .json") }
                    Button(attrs = { onClick { copyExport() } }) { Text("Copy") }
                }
            }
            if (exportText.isNotBlank()) TextArea(value = exportText, attrs = { classes("mono"); readOnly() })
            Div(attrs = { classes("section-rule") }) {}
            Div(attrs = { classes("muted") }) {
                Text("Identity credentials only: copy this to another origin when you want the same actor keys without importing posts.")
            }
            Div(attrs = { classes("row") }) {
                Button(attrs = { onClick { exportIdentityCredentials() } }) { Text("Export identity") }
                if (identityExportText.isNotBlank()) Button(attrs = { onClick { copyIdentityExport() } }) { Text("Copy identity") }
            }
            if (identityExportText.isNotBlank()) TextArea(value = identityExportText, attrs = { classes("mono"); readOnly() })
            Div(attrs = { classes("section-rule") }) {}
            TextArea(value = importText, attrs = { classes("mono"); placeholder("paste an exported blob to import"); onInput { importText = it.value } })
            Div(attrs = { classes("row") }) {
                Button(attrs = { onClick { importWorld() } }) { Text("Import") }
            }
            if (backupResult.isNotBlank()) Div(attrs = { classes("status") }) { Text(backupResult) }
        }
    }

    @Composable
    private fun NetLog() {
        Card("Activity") {
            if (logs.isEmpty()) Empty("—") else logs.takeLast(12).forEach { Div(attrs = { classes("muted") }) { Text(it) } }
        }
    }

    @Composable
    private fun Card(title: String, body: @Composable () -> Unit) {
        Div(attrs = { classes("card") }) {
            H3 { Text(title) }
            body()
        }
    }

    @Composable
    private fun CostField(value: String, onChange: (String) -> Unit) {
        Input(type = InputType.Text, attrs = {
            classes("cost"); placeholder("cost")
            title("How much to stake behind this — your backing (try 1)")
            value(value); onInput { onChange(it.value) }
        })
    }

    @Composable
    private fun Empty(text: String) {
        Div(attrs = { classes("muted") }) { Text(text) }
    }

    private fun quantity(token: String): Quantity? {
        val q = ActionGrammar.quantityOrNull(token.trim())
        if (q == null) status = "ERR bad cost '$token'"
        return q
    }

    private data class KnownPeerSession(val url: String, val role: String, val session: P2pSession)
}

/** One previewed action line plus its mutable confirm flag (executable lines default to confirmed). */
class PreviewRow(val preview: AgentActionPreview) {
    var confirmed by mutableStateOf(preview.executable)
}

/**
 * Identity gate shown before anything connects. A browser tab does not silently assume an identity: the user first
 * creates a fresh one or picks a saved one, and the choice is remembered *per tab* (via [Tab]/sessionStorage). That is
 * what lets two tabs of the same URL run as two distinct people — the precondition for them to discover each other and
 * exchange posts. Only once an identity is chosen is the [WebApp] (and its auto-discovery) brought up.
 */
class AppGate(
    initialNs: String?,
    private val discover: Boolean,
    private val signalUrl: String? = null,
    private val relayUrls: List<String> = listOf(DEFAULT_RELAY_URL),
) {
    private var app by mutableStateOf<WebApp?>(null)
    private var identityImportText by mutableStateOf("")
    private var identityImportResult by mutableStateOf("")

    init { initialNs?.let { open(it, SavedIdentities.nameOf(it)) } }

    @Composable
    fun Render() {
        val current = app
        if (current == null) Onboarding() else current.Render()
    }

    private fun open(ns: String, name: String?, ensureProfileOnBoot: Boolean = true) {
        val display = name ?: SavedIdentities.nameOf(ns) ?: ns
        SavedIdentities.ensure(ns, display)
        Tab.bind(ns)
        app = WebApp(
            ns, display, discover, signalUrl, relayUrls,
            onSwitchIdentity = ::switchIdentity,
            ensureProfileOnBoot = ensureProfileOnBoot,
        ).also { it.boot() }
    }

    /** Drop this tab's binding and return to the gate, so the user can adopt a different identity here. */
    private fun switchIdentity() { app?.stop(); Tab.clear(); app = null }

    private fun importIdentityCredentials() {
        val text = identityImportText.trim()
        if (text.isBlank()) { identityImportResult = "Paste exported identity credentials first."; return }
        val parsed = runCatching { js("JSON").parse(text) }
            .getOrElse { identityImportResult = "Import failed: not valid JSON."; return }
        if (parsed.format != IDENTITY_EXPORT_FORMAT) {
            identityImportResult = "Import failed: unrecognized identity credentials format."
            return
        }
        val keys = parsed.keys ?: run { identityImportResult = "Import failed: no credentials in blob."; return }
        val human = keys.human as? String ?: run { identityImportResult = "Import failed: missing human identity."; return }
        val importedNs = (parsed.sourceNs as? String)?.takeIf { it.isNotBlank() } ?: SavedIdentities.newNs()
        val importedName = (parsed.displayName as? String)?.takeIf { it.isNotBlank() }
            ?: SavedIdentities.nameOf(importedNs)
            ?: importedNs
        BrowserStore.setItem(identityStorageKey(importedNs), human)
        (keys.agent as? String)?.let { BrowserStore.setItem(identityStorageKey("$importedNs.agent"), it) }
        SavedIdentities.ensure(importedNs, importedName)
        identityImportText = ""
        identityImportResult = "Imported $importedName."
        open(importedNs, importedName, ensureProfileOnBoot = false)
    }

    @Composable
    private fun Onboarding() {
        Div(attrs = { classes("app", "onboarding") }) {
            Div(attrs = { classes("header") }) {
                H1 { Text("Invaluable") }
                Span(attrs = { classes("subtitle") }) { Text("Choose who this tab is — each tab can be a different person.") }
            }
            Div(attrs = { classes("gate") }) {
                GateCard("Create a new identity") {
                    var name by remember { mutableStateOf("") }
                    Div(attrs = { classes("muted") }) {
                        Text("Generates a fresh keypair locally — no signup, no server. Give it a name so you can tell tabs apart.")
                    }
                    Div(attrs = { classes("row") }) {
                        Input(type = InputType.Text, attrs = {
                            placeholder("display name, e.g. Alice"); value(name); onInput { name = it.value }
                        })
                        Button(attrs = { classes("primary"); onClick { open(SavedIdentities.newNs(), name.trim().ifBlank { "User" }) } }) { Text("Create") }
                    }
                }
                GateCard("Import identity credentials") {
                    Div(attrs = { classes("muted") }) {
                        Text("Paste an identity-only export from another origin. It imports private keys for this browser and opens that identity.")
                    }
                    TextArea(value = identityImportText, attrs = {
                        classes("mono")
                        placeholder("paste identity credentials JSON")
                        onInput { identityImportText = it.value }
                    })
                    Div(attrs = { classes("row") }) {
                        Button(attrs = { classes("primary"); onClick { importIdentityCredentials() } }) { Text("Import identity") }
                    }
                    if (identityImportResult.isNotBlank()) Div(attrs = { classes("status") }) { Text(identityImportResult) }
                }
                GateCard("Use a saved identity") {
                    val saved = SavedIdentities.list()
                    if (saved.isEmpty()) Div(attrs = { classes("muted") }) { Text("No identities saved in this browser yet.") }
                    else saved.forEach { p ->
                        Div(attrs = { classes("post") }) {
                            Div(attrs = { classes("post-head") }) {
                                Span(attrs = { classes("author") }) { Text(p.name) }
                                Span(attrs = { classes("ref") }) { Text("@${p.ns}") }
                            }
                            Div(attrs = { classes("actions") }) {
                                Button(attrs = { classes("primary"); onClick { open(p.ns, p.name) } }) { Text("Use") }
                            }
                        }
                    }
                }
            }
        }
    }

    @Composable
    private fun GateCard(title: String, body: @Composable () -> Unit) {
        Div(attrs = { classes("card") }) { H3 { Text(title) }; body() }
    }
}

/** Display name + namespace used only by the browser's local identity picker. */
private data class SavedIdentity(val ns: String, val name: String)

/**
 * Registry of identities created or used in this browser, persisted via [BrowserStore] (shared across tabs) so the
 * gate can offer them. The agent sub-identities (`<ns>.agent`) are never registered here — only human profiles.
 */
private object SavedIdentities {
    private const val KEY = "invaluable.profiles"

    fun list(): List<SavedIdentity> {
        val raw = BrowserStore.getItem(KEY) ?: return emptyList()
        val arr = runCatching { js("JSON").parse(raw) }.getOrNull() ?: return emptyList()
        val len = (arr.length as? Int) ?: return emptyList()
        val out = ArrayList<SavedIdentity>(len)
        for (i in 0 until len) {
            val p = arr[i] ?: continue
            val ns = (p.ns as? String) ?: continue
            out += SavedIdentity(ns, (p.name as? String) ?: ns)
        }
        return out
    }

    fun nameOf(ns: String): String? = list().firstOrNull { it.ns == ns }?.name

    /** Register [ns] (no-op if already present); first name wins so a later auto-default doesn't clobber a chosen one. */
    fun ensure(ns: String, name: String) {
        val cur = list()
        if (cur.any { it.ns == ns }) return
        val arr = js("[]")
        (cur + SavedIdentity(ns, name)).forEach { val o = js("{}"); o.ns = it.ns; o.name = it.name; arr.push(o) }
        BrowserStore.setItem(KEY, js("JSON").stringify(arr))
    }

    /** A fresh, collision-resistant namespace for a newly created identity. */
    fun newNs(): String = "u" + (js("Math.random().toString(36).slice(2, 8)") as String)
}

/**
 * Per-tab identity binding. Unlike [BrowserStore]/localStorage (shared by every tab of an origin), `sessionStorage` is
 * scoped to a single tab — so two tabs opened from the same URL can each remember a different chosen identity.
 */
private object Tab {
    private const val KEY = "invaluable.tab.ns"
    private fun ss(): dynamic = js("globalThis.window.sessionStorage")
    fun ns(): String? = runCatching { ss().getItem(KEY) as String? }.getOrNull()?.takeIf { it.isNotBlank() }
    fun bind(ns: String) { runCatching { ss().setItem(KEY, ns) } }
    fun clear() { runCatching { ss().removeItem(KEY) } }
}

private fun localSignalUrl(raw: String?): String? {
    val trimmed = raw?.trim()
    if (trimmed in listOf("0", "false", "off")) return null
    if (!trimmed.isNullOrBlank() && (trimmed.startsWith("ws://") || trimmed.startsWith("wss://"))) return trimmed
    val port = trimmed?.toIntOrNull() ?: Signaling.DEFAULT_PORT
    if (trimmed == null && window.location.protocol == "https:") return null
    return "ws://127.0.0.1:$port"
}

private fun relayUrls(params: dynamic): List<String> {
    val all = (params.getAll("relay") as Array<String>).flatMap { it.split(',') }.map { it.trim() }
    if (all.any { it in listOf("0", "false", "off") }) return emptyList()
    return normalizePeerUrls(all.ifEmpty { configuredRelayUrls().ifEmpty { listOf(DEFAULT_RELAY_URL) } }).also { urls ->
        require(urls.all(OpaqueRelaySession::isUrl)) {
            "Relay URLs must use transport=opaque-v2 with a valid route"
        }
    }
}

private fun opaqueRelayCursorKey(url: String): String {
    val encoded = js("encodeURIComponent(url)") as String
    return "invaluable.opaque-relay.cursor.$encoded"
}

private fun isKnownPeerClosed(session: P2pSession): Boolean = when (session) {
    is OpaqueRelaySession -> session.isClosed
    else -> ! session.isOpen
}

private fun configuredRelayUrls(): List<String> {
    val configured = js("globalThis.INVALUABLE_RELAY_URLS")
    if (configured == null) return emptyList()
    if (js("Array.isArray(configured)") as Boolean) return (configured as Array<String>).flatMap { it.split(',') }
    return ((configured as? String) ?: return emptyList()).split(',')
}

private fun normalizePeerUrls(urls: List<String>): List<String> {
    val normalized = LinkedHashSet<String>()
    for (url in urls) {
        val trimmed = url.trim()
        if (trimmed.startsWith("ws://") || trimmed.startsWith("wss://")) normalized += trimmed
    }
    return normalized.toList()
}
